// Iter58+2 — Onboard tenant modal used by the public /tenants page.
//
// Reuses the same backend endpoint (POST /admin/tenants) and master-data
// (tenant_types) that the platform-admin AdminTenants page uses. Form fields
// mirror the AdminTenants TenantCard editor: name, initials, tenant type,
// layer, plan, plus a single optional primary contact (name/email/phone).
//
// Permission gate: only platform_admin role can actually save (the backend
// enforces this); the modal renders a read-only notice for everyone else.
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { useT } from "../lib/i18n";
import {
  useCreateTenant, useTenantTypes, useAddTenantContact,
} from "../api/sitroom";
import { pickName } from "./MasterDataAtoms";

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const inputStyle = {
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize: 12,
  width: "100%",
  padding: "6px 8px",
  borderRadius: 4,
  outline: "none",
};

const Field = ({ label, required, children }) => (
  <div>
    <div style={mono(9, "#9198b0", undefined, "1.2px")} className="uppercase mb-1">
      {label}{required && " *"}
    </div>
    {children}
  </div>
);

export const OnboardTenantModal = ({ onClose, onCreated }) => {
  const { user } = useAuth();
  const { locale } = useT();
  const addToast = useStore((s) => s.addToast);
  const canSave = user?.role === "platform_admin";

  const { data: tenantTypes = [] } = useTenantTypes(true);
  const createMut = useCreateTenant();
  const addContactMut = useAddTenantContact();

  const ttByKey = useMemo(() => Object.fromEntries(tenantTypes.map((t) => [t.key, t])), [tenantTypes]);

  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [layer, setLayer] = useState("");
  const [plan, setPlan] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Auto-suggest initials from name when user hasn't typed any.
  useEffect(() => {
    if (initials || !name.trim()) return;
    const parts = name.trim().split(/\s+/);
    const i = (parts[0]?.[0] || "") + (parts[1]?.[0] || parts[0]?.[1] || "");
    setInitials(i.toUpperCase().slice(0, 3));
    // intentionally not adding `initials` to deps — only run on name typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  // Auto-fill layer when a tenant type is selected.
  const onTypeChange = (val) => {
    setTypeKey(val);
    const tt = ttByKey[val];
    if (tt?.layer) setLayer(String(tt.layer));
  };

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    if (!canSave) { setError("Only platform_admin can onboard tenants"); return; }
    setBusy(true);
    try {
      const tt = ttByKey[typeKey];
      const body = {
        name: name.trim(),
        tenant_type_key: typeKey || null,
        layer: layer ? parseInt(layer, 10) : (tt?.layer || null),
        plan: plan.trim() || null,
        initials: initials.trim() || null,
      };
      const created = await createMut.mutateAsync(body);
      // Optional primary contact (best-effort, doesn't block tenant creation success).
      if (contactName.trim() && created?.id) {
        try {
          await addContactMut.mutateAsync({
            tenant_id: created.id,
            body: {
              name: contactName.trim(),
              role: "primary",
              email: contactEmail.trim() || undefined,
              phone: contactPhone.trim() || undefined,
            },
          });
        } catch (_) { /* tenant created, contact failed — surface as warning */ }
      }
      addToast({ type: "success", title: "Tenant onboarded", body: created?.name || name });
      onCreated?.(created);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Save failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onClose}
      data-testid="onboard-tenant-modal-overlay"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.18)",
          width: "min(640px, 96vw)", maxHeight: "92vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="onboard-tenant-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
            Onboard Tenant
          </span>
          <button
            type="button" onClick={onClose}
            style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }}
            data-testid="onboard-tenant-modal-close"
          >×</button>
        </div>

        {!canSave && (
          <div className="mb-3 p-2 rounded" style={{ background: "rgba(243,177,74,0.08)", border: "1px solid rgba(243,177,74,0.30)", color: "#f3b14a", ...mono(10) }} data-testid="onboard-tenant-permission-note">
            Only platform_admin users can onboard new tenants. You can preview the form, but Save is disabled.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" required>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              style={inputStyle} placeholder="e.g. BMW Regensburg"
              data-testid="onboard-tenant-name"
            />
          </Field>
          <Field label="Initials">
            <input
              type="text" value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 3))}
              style={inputStyle} placeholder="auto from name" maxLength={3}
              data-testid="onboard-tenant-initials"
            />
          </Field>

          <Field label="Tenant type">
            <select
              value={typeKey} onChange={(e) => onTypeChange(e.target.value)}
              style={inputStyle}
              data-testid="onboard-tenant-type"
            >
              <option value="">— select —</option>
              {tenantTypes.map((tt) => (
                <option key={tt.key} value={tt.key}>
                  {`${pickName(tt, locale)} (L${tt.layer})`}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Layer">
            <select
              value={layer} onChange={(e) => setLayer(e.target.value)}
              style={inputStyle}
              data-testid="onboard-tenant-layer"
            >
              <option value="">—</option>
              {[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>{`L${l}`}</option>)}
            </select>
          </Field>

          <Field label="Plan">
            <input
              type="text" value={plan} onChange={(e) => setPlan(e.target.value)}
              style={inputStyle} placeholder="Enterprise / Pro / Starter"
              data-testid="onboard-tenant-plan"
            />
          </Field>
          <div /> {/* spacer */}

          <div className="sm:col-span-2 mt-2 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={mono(9, "#9198b0", 700, "1.2px")} className="uppercase mb-2">
              Primary contact (optional)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                style={inputStyle} placeholder="Name"
                data-testid="onboard-tenant-contact-name"
              />
              <input
                type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                style={inputStyle} placeholder="Email"
                data-testid="onboard-tenant-contact-email"
              />
              <input
                type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                style={inputStyle} placeholder="Phone"
                data-testid="onboard-tenant-contact-phone"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", ...mono(11) }} data-testid="onboard-tenant-error">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="onboard-tenant-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={submit} disabled={busy || !canSave}
            className="px-3 py-2 rounded-md"
            style={{
              background: "#185FA5", color: "#fff",
              fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12,
              cursor: busy || !canSave ? "not-allowed" : "pointer",
              opacity: busy || !canSave ? 0.6 : 1, border: "none",
            }}
            data-testid="onboard-tenant-submit"
          >
            {busy ? "Onboarding…" : "Onboard"}
          </button>
        </div>
      </div>
    </div>
  );
};
